import {
  Box,
  Flex,
  Image,
  Link,
  TagFilledIcon,
  Typography,
} from '@iheartjane/reefer';
import type {
  Breadcrumb,
  ReservationModeLabels,
  SpecialType,
  StoreSpecialReservationModes,
} from '@iheartjane/types';

import { getReservationModeLabel } from '../../utils/getReservationModeLabels';
import {
  getDiscountLabel,
  getSpecialType,
  hasSingleReservationMode,
} from '../../utils/specials/storeSpecial';
import styles from './specialCard.module.css';

export interface SpecialCardProps {
  /** Breadcrumb navigation data for the Link component */
  breadcrumbs?: Breadcrumb[];

  /** Custom badge text, overrides auto-generated discount label */
  customSpecialBadge?: string | null;

  /** Discount value*/
  discountAmount?: string | null;

  /** Type of discount */
  discountType: string;

  /** URL of the image displayed on the card if applicable */
  imageSrc?: string | null;

  /** Flag to control premium-specific styling when no image is provided */
  isPremium?: boolean;

  /** Function called when card is clicked */
  onClick?: () => void;

  /** Custom labels for reservation modes (overrides defaults) */
  reservationModeLabels?: ReservationModeLabels;

  /** Reservation modes available for this special */
  reservationModes?: StoreSpecialReservationModes | null;

  /** Type of special offer */
  specialType: SpecialType;

  /** Store-specific compliance language customization */
  storeComplianceLanguageSettings?: Record<string, string | null> | null;

  /** Name of the special */
  title: string;

  /** Navigation URL for the card link */
  to?: string;
}
export const SpecialCard = ({
  breadcrumbs,
  customSpecialBadge,
  discountAmount,
  discountType,
  imageSrc,
  isPremium,
  onClick,
  reservationModes,
  reservationModeLabels,
  specialType,
  storeComplianceLanguageSettings,
  title,
  to,
}: SpecialCardProps) => {
  const reservationMode = hasSingleReservationMode(reservationModes);
  const specialAvailability =
    reservationMode &&
    getReservationModeLabel(reservationMode, reservationModeLabels);

  return (
    <Link
      className={styles['specialCardLink']}
      to={to}
      {...(breadcrumbs && { state: { breadcrumbs } })}
      onClick={onClick}
      ariaLabel={`Card for ${title}`}
      variant="minimal"
      data-testid="special-card"
    >
      <Flex
        className={styles['specialCard']}
        flexDirection="column"
        height="100%"
        border="transparent-black-ten"
        borderRadius="lg"
        overflow="hidden"
      >
        {imageSrc ? (
          <Image
            width="100%"
            altText={`Promotional image for ${title}`}
            src={imageSrc}
            sizes="card-lg"
            responsive
            aspectRatio={2 / 1}
          />
        ) : (
          <Flex
            className={styles['specialCardDefaultImage']}
            width="100%"
            alignItems="center"
            justifyContent="center"
            background={isPremium ? 'info' : 'grays-ultralight'}
            gap={8}
          >
            <TagFilledIcon size="lg" color={isPremium ? 'text-info' : 'info'} />
            <Typography
              variant="header-bold"
              as="p"
              maxLines={2}
              branded
              color={isPremium ? 'text-info' : undefined}
            >
              {customSpecialBadge
                ? customSpecialBadge
                : getDiscountLabel({
                    discountAmount,
                    discountType,
                    specialType,
                    storeComplianceLanguageSettings,
                  })}
            </Typography>
          </Flex>
        )}
        <Box p={24} width="100%" background="grays-white">
          <Flex justifyContent="space-between">
            <Typography color="info">
              {getSpecialType(specialType, storeComplianceLanguageSettings)}
            </Typography>
            {specialAvailability && (
              <Typography variant="mini" color="text-light" ml={16}>
                {specialAvailability} only
              </Typography>
            )}
          </Flex>
          <Typography variant="body-bold" maxLines={2}>
            {title}
          </Typography>
        </Box>
      </Flex>
    </Link>
  );
};
